import React, { createContext, useContext, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import usePageState from '@/hooks/usePageState';

import { useGlobalConfig } from '@/components/GlobalContext';

import { useConfig } from '@/appconfig/ConfigContext';
import { CraveErrorCode, MaturityLevels, UM_To_RTE_MaturityMap } from '@/enums';
import {
  addProfile,
  login as authLogin,
  logout as authLogout,
  emailExists as checkEmailExists,
  deleteProfile,
  enterPin,
  getProfiles as fetchProfiles,
  getAccessToken,
  getAccountDetails,
  getProfileById,
  getStoredAccountDetails,
  getStoredProfile,
  refreshToken as refreshAccessToken,
  resolveAutoSelectedProfile,
  saveProfile,
  setUserOnboarded,
  setUserPlaybackLanguages,
  updateProfile,
} from '@/services/auth';
import { logoutUser } from '@/services/customerDataSuite';
import { Language, Maturity } from '@/services/graphQL/api/generated/types_web';
import { getSystem } from '@/system';
import type { AccountDetails, AuthContextType, Profile } from '@/types';
import { deeplinkStorage } from '@/utils/deeplinkStorage';
import { FATAL_ERROR_EVENT, type FatalErrorEventDetail } from '@/utils/fatalError';
import { getSystemLanguage } from '@/utils/getSystemLanguage';
import { normalizeDeviceLanguage } from '@/utils/normalizeDeviceLanguage';
import { getProfilePattern } from '@/utils/profilePattern';
import { SESSION_INVALIDATED_EVENT } from '@/utils/sessionInvalidated';

import { CustomEvent } from '../../services/customerDataSuite/constants';
import {
  CommonAttributeName,
  CustomAttributeName,
  MonitoringEventName,
  SignOutReason,
  log,
  monitorEvent,
  setCustomMonitoringAttribute,
} from '../appMonitoringSuite';
import {
  getPlatformId,
  getProfileType,
  logEvent,
  setUserAttributes,
} from '../customerDataSuite';
import { flushCache } from '../graphQL';
import { flushQueryClient } from '../graphQL/queryClient';
import { useSmartId } from '../userEngagementSuite/SmartIdContext';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Credentials live in storage but auth state lives in React, so any entry that
// skips SplashScreen (a resumed route, a reload, a device wake) would otherwise
// render a fully credentialed user as signed out.
const getHydratedLogin = () => {
  try {
    return getStoredProfile();
  } catch (error) {
    console.error('Failed to retrieve stored profile:', error);
    return undefined;
  }
};

export const PROFILE_DEFAULT = {
  nickname: '',
  hasPin: false,
  master: false,
  maturity: MaturityLevels.ADULT,
  uiLanguage: Language.En,
  playbackLanguages: [Language.En],
};

// The PIN was accepted but the follow-up refresh failed and already signed the user out.
// Distinct from a rejected PIN so the screen neither reports one nor spends an attempt.
export const PIN_REFRESH_FAILED = 'PIN_REFRESH_FAILED';

// The PIN was accepted but the profile behind it could not be read, so its maturity and
// language are unknown. Distinct from a rejected PIN for the same reason as above.
export const PROFILE_LOOKUP_FAILED = 'PROFILE_LOOKUP_FAILED';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const hydratedLogin = getHydratedLogin();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [token, setToken] = useState<string | null>(
    () => hydratedLogin?.accessToken ?? null
  ); // Store token separately
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  // Hydrated lazily rather than in an effect: a signed-out first paint makes
  // /profile redirect to /frontdoor before an effect could correct it. A stored
  // token that is actually dead is demoted by the existing guards, which sign
  // out when getAccessToken() / the profile fetch fails.
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    Boolean(hydratedLogin?.accessToken)
  );
  const [isInitializing, setIsInitializing] = useState(true);
  // Bumped by applySession/logout. The app query's token rides in a header rather than a
  // variable, so Apollo cannot see a session change; this is the signal it reads instead.
  const [sessionId, setSessionId] = useState(0);
  const [pinRemoved, setPinRemoved] = useState(false);
  const [fatalErrorCode, setFatalErrorCode] = useState<CraveErrorCode | null>(null);
  const [profileToEdit, setProfileToEdit] = useState<Partial<Profile> | null>(null);
  const [profileEditBaseline, setProfileEditBaseline] = useState<Partial<Profile> | null>(
    null
  );
  // Lazy initialization: read from storage on first render with error handling
  const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(() => {
    try {
      return getStoredAccountDetails();
    } catch (error) {
      console.error('Failed to retrieve stored account details:', error);
      return null;
    }
  });
  const {
    localization,
    setLanguage,
    setMaturity,
    setPlaybackLanguage,
    setUserExperienceInformation,
  } = useGlobalConfig(); // Localization object from context
  const bduLocale = localization?.['bdu-login'];
  const system = getSystem();

  const config = useConfig();
  const { replacePageState } = usePageState();
  const queryClient = useQueryClient();
  const { smartId } = useSmartId();

  // Show the error overlay when non-React code (e.g. token refresh) reports a fatal error.
  useEffect(() => {
    const handleFatalError = (event: Event) => {
      const detail = (event as CustomEvent<FatalErrorEventDetail>).detail;
      if (detail?.code) setFatalErrorCode(detail.code);
    };
    window.addEventListener(FATAL_ERROR_EVENT, handleFatalError);
    return () => window.removeEventListener(FATAL_ERROR_EVENT, handleFatalError);
  }, []);

  // The platform can clear the Apollo store from outside the auth layer, which leaves the app
  // query stuck loading. The dispatcher owns the flush and fires only once it has settled.
  useEffect(() => {
    const handleSessionInvalidated = () => setSessionId((id) => id + 1);
    window.addEventListener(SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
    return () =>
      window.removeEventListener(SESSION_INVALIDATED_EVENT, handleSessionInvalidated);
  }, []);

  // Follow the device language only while signed out; a signed-in profile's own
  // language setting takes over once authenticated.
  useEffect(() => {
    if (isAuthenticated) return;

    const unsubscribe = system.listenForDeviceLanguageChanges?.((deviceLanguage) => {
      setLanguage(normalizeDeviceLanguage(deviceLanguage));
    });

    return () => unsubscribe?.();
  }, [isAuthenticated]);

  // Function to login the user and fetch details
  const loginHandler = async (username: string, password: string) => {
    try {
      const response = await authLogin(
        username,
        password,
        config?.services?.um?.loginVersion
      );
      if (!response || !response.access_token) {
        throw new Error(bduLocale?.error?.['login-failed']);
      } else {
        const { access_token, refresh_token, expires_in, scope, localization } = response;
        const expires = Math.floor(Date.now() / 1000) + expires_in;

        // Save token
        setToken(access_token);
        setIsAuthenticated(true);

        // Save the profile (including token, refreshToken and expires)
        saveProfile({
          accessToken: access_token,
          refreshToken: refresh_token,
          expires: expires,
          scope: scope,
          localization: localization,
        });

        // Clears the store, so it has to signal or nothing refetches the app query.
        await invalidateSessionHandler();

        await getAccountDetailsHandler({
          accessToken: access_token,
          expires: expires,
        });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message);
    }
  };

  // Function to validate PIN
  const enterPinHandler = async (
    profileId: string,
    profilePin: string,
    isEditMode?: boolean
  ) => {
    try {
      // Directly validate PIN
      const scopedAccessToken = await enterPin(
        profileId,
        profilePin,
        config?.services?.um?.loginVersion,
        isEditMode
      );

      // Try to get the profile if we don't already have it
      let matchingProfile = profiles.find((p) => p.id === profileId);

      if (!matchingProfile) {
        try {
          if (profiles.length === 0) {
            // Try loading all profiles
            const fetchedProfiles = await getProfilesHandler();
            matchingProfile = fetchedProfiles.find((p) => p.id === profileId);
          }

          // If we still don't have the profile, try loading it directly
          if (!matchingProfile) {
            const loadedProfile = await getProfileByIDHandler(profileId);
            if (loadedProfile) {
              matchingProfile = loadedProfile;
            }
          }
        } catch (profileError) {
          console.error('Failed to load profile details:', profileError);
        }

        // A lookup that returns nothing leaves maturity and language unknown, and applying a
        // session on an id alone carries the previous profile's rating into this one. Edit
        // mode applies no session, so it has no such stake and still succeeds.
        if (!matchingProfile && !isEditMode) {
          log('AuthContext: PIN accepted but the profile could not be loaded', 'error');
          throw new Error(PROFILE_LOOKUP_FAILED);
        }
      }

      // enterPin has already scoped storage to this profile, so everything below runs on the
      // new token. Only fall back to a second /login if the PIN response carried no token.
      if (matchingProfile && !isEditMode) {
        if (!scopedAccessToken && !(await refreshTokenHandler(profileId, profilePin))) {
          // Signed out already, so applying a session below would revive it on a dead token.
          log('AuthContext: PIN accepted but token refresh failed', 'error');
          throw new Error(PIN_REFRESH_FAILED);
        }

        const storedProfile = getStoredProfile();
        if (storedProfile?.accessToken) {
          await getAccountDetailsHandler({
            accessToken: storedProfile.accessToken,
            expires: storedProfile.expires,
          });
        }

        await applySessionHandler(
          matchingProfile,
          scopedAccessToken ?? storedProfile?.accessToken
        );
      }

      setIsAuthenticated(true);
    } catch (error: any) {
      console.error('Enter PIN error:', error);
      throw new Error(error.message);
    }
  };

  // Logout logic
  const logoutHandler = async (reason?: SignOutReason) => {
    reason &&
      monitorEvent(MonitoringEventName.SIGNOUT, {
        [CustomAttributeName.SIGNOUTREASON]: reason,
      });

    logEvent(CustomEvent.USER_SIGNED_OUT, {
      profile_id: selectedProfile?.id || '',
      profile_type: selectedProfile?.maturity
        ? getProfileType(selectedProfile.maturity)
        : '',
      platform_id: getPlatformId(system.id),
      smart_id: smartId || '',
    });
    logoutUser();
    try {
      authLogout();
    } catch (error) {
      console.error('Failed to clear stored profile during logout:', error);
    }
    setToken(null);
    setProfiles([]);
    setSelectedProfile(null);
    setAccountDetails(null);
    setIsAuthenticated(false);
    queryClient.clear();
    try {
      await flushSessionCaches();
    } catch (error) {
      console.warn('AuthContext: logout cache flush failed:', error);
    }
    setSessionId((id) => id + 1);
    setUserExperienceInformation();
    resetMonitoringAttributesOnLogout();
    setLanguage(getSystemLanguage());
    setMaturity(Maturity.Adult);

    system.logout?.();
  };

  // Function to check if the email exists
  const emailExistsHandler = async (email: string): Promise<boolean> => {
    return checkEmailExists(email);
  };

  const addProfileHandler = async (
    profile: Partial<Profile>
  ): Promise<Profile | null> => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        log('AuthContext: Cannot add profile without an access token', 'error');
        return null;
      }

      if (profile) {
        if (profile.pin?.trim() === '') {
          delete profile.pin;
        }
        if (profile.hasPin) {
          delete profile.hasPin;
        }
      }

      // TODO: Update config profileVersion before testing
      const addedProfile = await addProfile(
        accessToken,
        profile,
        'v2' //config?.services?.um?.profileVersion
      );
      if (addedProfile) {
        // Assign pattern based on number of existing profiles
        addedProfile.avatarUrl = getProfilePattern(profiles.length);
        setProfiles([...profiles, addedProfile]);
        return addedProfile;
      }
      return null;
    } catch (error) {
      log(`AuthContext: Error adding profile: ${error}`, 'error');
      return null;
    }
  };

  const updateProfileHandler = async (
    profile: Partial<Profile>,
    selectProfile: boolean = true
  ): Promise<Profile | null> => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        log('AuthContext: Cannot update profile without an access token', 'error');
        return null;
      }

      // Find existing profile to preserve pattern
      const existingProfile = profiles.find((p) => p.id === profile.id);
      const existingPattern = existingProfile?.avatarUrl;

      // TODO: Update config profileVersion before testing
      const updatedProfile = await updateProfile(
        accessToken,
        profile,
        'v2' //config?.services?.um?.profileVersion
      );

      if (updatedProfile) {
        // Preserve existing pattern or assign new one
        const profileIndex = profiles.findIndex((p) => p.id === updatedProfile.id);
        updatedProfile.avatarUrl = getProfilePattern(profileIndex, existingPattern);

        if (selectProfile) {
          setSelectedProfile(updatedProfile);
        }

        const updatedProfiles = profiles.map((p) => {
          if (p.id === updatedProfile.id) {
            return {
              ...updatedProfile,
              avatarUrl: updatedProfile.avatarUrl,
            };
          }
          return p;
        });

        setProfiles(updatedProfiles);

        return updatedProfile;
      }
      return null;
    } catch (error) {
      log(`AuthContext: Error updating profile: ${error}`, 'error');
      return null;
    }
  };

  const deleteProfileHandler = async (
    profile: Partial<Profile>
  ): Promise<Profile | null> => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        log('AuthContext: Cannot delete profile without an access token', 'error');
        return null;
      }

      const deletedProfile = await deleteProfile(
        accessToken,
        profile.id as string,
        'v1.1'
      );
      const updatedProfiles = profiles.filter((p) => p.id !== profile.id);
      setProfiles(updatedProfiles);

      return deletedProfile;
    } catch (error) {
      log(`AuthContext: Error deleting profile: ${error}`, 'error');
      return null;
    }
  };

  const refetchProfilesHandler = async (selectedProfile: Profile) => {
    const accessToken = await getAccessToken();
    const fetchedProfiles = await fetchProfiles(accessToken);

    if (fetchedProfiles.length > 0) {
      fetchedProfiles.forEach((profile, index) => {
        profile.avatarUrl = getProfilePattern(index, profile.avatarUrl);
      });

      setProfiles(fetchedProfiles);
    }

    const matchingProfile = fetchedProfiles.find((p) => p.id === selectedProfile.id);
    if (matchingProfile) {
      if (matchingProfile?.playbackLanguages) {
        setPlaybackLanguage(matchingProfile.playbackLanguages);
      }
      setSelectedProfile(matchingProfile);
    } else {
      setSelectedProfile(selectedProfile);
    }
  };

  /**
   * Language and maturity live in GlobalContext state only - maturity in
    particular falls back to Adult when unset - so any path that selects a
    profile has to apply them or the app queries content as the wrong user.
   * @param profile
   */
  const applyProfilePreferences = (profile: Profile) => {
    // uiLanguage comes off the profile API unnormalized, and both of these end up as
    // localization keys, which are 'EN' / 'FR'.
    const uiLanguage = profile.uiLanguage
      ? normalizeDeviceLanguage(profile.uiLanguage)
      : undefined;

    if (uiLanguage) {
      setLanguage(uiLanguage);
    }

    if (profile.playbackLanguages) {
      setPlaybackLanguage(profile.playbackLanguages);
    } else if (uiLanguage) {
      setPlaybackLanguage([uiLanguage]);
    }

    if (profile.maturity) {
      setMaturity(UM_To_RTE_MaturityMap(profile.maturity as MaturityLevels));
    }
  };

  // Fetch profiles logic
  const getProfilesHandler = async (): Promise<Profile[]> => {
    const accessToken = await getAccessToken();

    try {
      // TODO: Update config profileVersion before testing
      const profiles = await fetchProfiles(
        accessToken,
        'v2' //config?.services?.um?.profileVersion
      );
      setIsAuthenticated(true);

      // Assign or preserve patterns for each profile
      profiles.forEach((profile, index) => {
        profile.avatarUrl = getProfilePattern(index, profile.avatarUrl);
      });

      setProfiles(profiles);

      // Auto-select only when the account has a single profile. With more than
      // one, the user picks every launch, so nothing is selected here and the
      // caller shows /profile. A PIN-protected profile is never selected here -
      // it is only ever entered through /profile/enter-pin, which is also what
      // scopes the token to it. Callers route that case; see the entry guard in
      // app/app.tsx.
      const autoSelectedProfile = resolveAutoSelectedProfile(profiles);
      if (autoSelectedProfile && !autoSelectedProfile.hasPin) {
        applyProfilePreferences(autoSelectedProfile);
        setSelectedProfile(autoSelectedProfile);
      }
      return profiles;
    } catch (error) {
      console.log(`Error fetching profiles:, ${error}`);
      log('GetProfiles: Error Fetching getProfiles', 'error');
      return [];
    }
  };

  // Awaited by both verbs below, so clearStore() has always settled before sessionId moves and
  // consumers never refetch into a store that is still being torn down.
  const flushSessionCaches = async () => {
    flushQueryClient();
    await flushCache();
  };

  /**
   * The single place a session is applied. Every setter below lands in one commit, so the app
   * query never fires against a half-applied session, and sessionId is what tells consumers to
   * refetch.
   */
  const applySessionHandler = async (profile: Profile, accessToken?: string) => {
    try {
      await flushSessionCaches();
    } catch (error) {
      // The store may already be torn down; the session still has to apply or nothing
      // repairs the app query, so this is logged rather than rethrown to the caller.
      console.warn('AuthContext: applySession cache flush failed:', error);
      log('AuthContext: applySession cache flush failed', 'warn');
    }

    setToken(accessToken ?? getHydratedLogin()?.accessToken ?? null);
    setSelectedProfile(profile);
    applyProfilePreferences(profile);
    setSessionId((id) => id + 1);
  };

  /**
   * For the paths that clear the store without choosing a profile: sign-in scopes the account
   * before any profile exists. Clearing the store wipes the app query's data, so the flush and
   * the signal have to travel together or nothing refetches it.
   */
  const invalidateSessionHandler = async () => {
    try {
      await flushSessionCaches();
    } catch (error) {
      console.warn('AuthContext: invalidateSession cache flush failed:', error);
      log('AuthContext: invalidateSession cache flush failed', 'warn');
    }

    setSessionId((id) => id + 1);
  };

  // Refresh token logic. Reports success: on failure this signs the user out, so callers that
  // go on to apply a session have to bail rather than re-populate the state logout just cleared.
  const refreshTokenHandler = async (
    profileId?: string,
    profilePin?: string
  ): Promise<boolean> => {
    try {
      const response = await refreshAccessToken(
        profileId,
        profilePin,
        config?.services?.um?.loginVersion
      );
      setToken(response.access_token);
      saveProfile({
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        expires: Math.floor(Date.now() / 1000) + response.expires_in,
        scope: response.scope,
        localization: response.localization,
      });
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // 429: session is still valid and the overlay is already shown — don't sign out.
      if (error instanceof Error && error.message === '429') {
        log('AuthContext: Rate limit (429) hit during token refresh', 'error');
        return false;
      }
      log('AuthContext: Refresh Token Fetch Failed. Redirecting to frontdoor', 'error');
      logoutHandler()
        .then(() => {
          if (!deeplinkStorage.isProcessing()) {
            replacePageState('/frontdoor');
            return;
          }
          // The deep-link guard suppresses the redirect, which would otherwise leave the
          // user on a screen the session behind it no longer exists for.
          log(
            'AuthContext: Refresh failed during deep-link processing, raising the overlay',
            'error'
          );
          setFatalErrorCode(CraveErrorCode.CONNECTION_ERROR_4003);
        })
        .catch((logoutError) => {
          // Nothing above ran, so the redirect never fired either; the overlay is the only
          // thing left that tells the user their session is gone.
          log(
            `AuthContext: Sign-out after a failed refresh threw: ${logoutError}`,
            'error'
          );
          setFatalErrorCode(CraveErrorCode.CONNECTION_ERROR_4003);
        }); // Logout on failure to refresh
      return false;
    }
  };

  // Fetch account details only if not already cached
  const getAccountDetailsHandler = async (
    storedProfile: { accessToken: string; expires: number } | undefined
  ) => {
    try {
      if (accountDetails) {
        return;
      }

      if (!storedProfile) {
        storedProfile = await getStoredProfile();
      }
      if (storedProfile) {
        const { accessToken } = storedProfile;
        if (accessToken) {
          await getAccountDetails(accessToken).then((details) => {
            setAccountDetails(details);
            setUserAttributes(details);
          });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const setUserOnboardedHandler = async () => {
    const accessToken = await getAccessToken();
    await setUserOnboarded(accessToken, selectedProfile?.id as string);
  };

  const setUserPlaybackLanguagesHandler = async (playbackLanguages: string[]) => {
    const accessToken = await getAccessToken();
    await setUserPlaybackLanguages(
      accessToken,
      selectedProfile?.id as string,
      playbackLanguages
    );
  };

  const getProfileByIDHandler = async (id: string): Promise<Profile | null> => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        log('AuthContext: Cannot get profile without an access token', 'error');
        return null;
      }

      // TODO: Update config profileVersion before testing
      const profile = await getProfileById(
        id,
        accessToken,
        'v2' //config?.services?.um?.profileVersion
      );
      if (profile) {
        return profile;
      }
      return null;
    } catch (error) {
      log(`AuthContext: Error getting profile: ${error}`, 'error');
      return null;
    }
  };

  const resetProfileToEdit = () => {
    setProfileToEdit({
      ...PROFILE_DEFAULT,
    });
    setProfileEditBaseline(null);
    setPinRemoved(false);
  };

  // Gets users profiles, renews tokens when necessary
  const checkUserProfiles = async (accessToken: string): Promise<Profile[]> => {
    try {
      const profiles = await fetchProfiles(accessToken, 'v2');
      return profiles;
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        throw e;
      }

      try {
        await refreshTokenHandler();
        const accessToken = await getAccessToken();
        const profiles = await fetchProfiles(accessToken, 'v2');
        return profiles;
      } catch (e) {
        throw e;
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        profiles,
        selectedProfile,
        setIsAuthenticated,
        setIsInitializing,
        enterPin: enterPinHandler,
        getAccountDetails: getAccountDetailsHandler,
        applySession: applySessionHandler,
        invalidateSession: invalidateSessionHandler,
        sessionId,
        login: loginHandler,
        logout: logoutHandler,
        refreshToken: refreshTokenHandler,
        emailExists: emailExistsHandler,
        getProfiles: getProfilesHandler,
        addProfile: addProfileHandler,
        updateProfile: updateProfileHandler,
        deleteProfile: deleteProfileHandler,
        getProfileById: getProfileByIDHandler,
        isAuthenticated,
        isInitializing,
        profileToEdit,
        pinRemoved,
        setPinRemoved,
        setProfileToEdit,
        resetProfileToEdit,
        profileEditBaseline,
        setProfileEditBaseline,
        setUserOnboarded: setUserOnboardedHandler,
        setUserPlaybackLanguages: setUserPlaybackLanguagesHandler,
        refetchProfiles: refetchProfilesHandler,
        checkUserProfiles: checkUserProfiles,
        accountDetails,
        fatalErrorCode,
        clearFatalError: () => setFatalErrorCode(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const resetMonitoringAttributesOnLogout = () => {
  setCustomMonitoringAttribute(CommonAttributeName.BMID, '');
  setCustomMonitoringAttribute(CommonAttributeName.PROFILEID, '');
  setCustomMonitoringAttribute(CommonAttributeName.ISMASTERPROFILE, false);
  setCustomMonitoringAttribute(CommonAttributeName.MATURITY, '');
  setCustomMonitoringAttribute(CommonAttributeName.SUBSCRIPTIONS, '');
  setCustomMonitoringAttribute(CommonAttributeName.ACCOUNTLANGUAGE, '');
  setCustomMonitoringAttribute(CommonAttributeName.LANGUAGE, '');
  setCustomMonitoringAttribute(CommonAttributeName.TOKENSCOPE, '');
};
